import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CBadge,
  CWidgetStatsA,
  CSpinner,
  CButton,
  CButtonGroup,
} from "@coreui/react";
import { CChartBar } from "@coreui/react-chartjs";
import { cilPeople, cibAppStore, cilCalendar, cilChart } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import formatter from "src/reusables/formatter";
import { getDashboardMetrics, getMostVisitedStores, getVisitTrends } from "src/services/Analytics";
import SalesPicker from "src/components/SalesPicker";
import { format, isWeekend } from "date-fns";
import ls from "local-storage";
import Moment from "moment";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file

function Dashboard() {
  const useWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = window;
    const comparison = width / height;
    const [windowDimensions, setWindowDimensions] = useState({
      width,
      height,
      comparison
    });

    useEffect(() => {
      function handleResize() {
        const { innerWidth: width, innerHeight: height } = window;
        const comparison = width / height;
        setWindowDimensions({ width, height, comparison });
      }

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowDimensions;
  };

  const [refetch, setRefetch] = useState(false);
  const [dateRange, setDateRange] = useState([{
    startDate: new Date(`${Moment(new Date()).startOf('month')}`),
    endDate: new Date()
  }]);

  // Unified date range for top 10 sections
  const [topSectionsDateRange, setTopSectionsDateRange] = useState([{
    startDate: new Date(`${Moment(new Date()).startOf('month')}`),
    endDate: new Date()
  }]);

  // Single show date picker state for top 10 sections
  const [showTopSectionsDatePicker, setShowTopSectionsDatePicker] = useState(false);

  // Get filter parameters from localStorage
  const getFilterParams = () => {
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");
    
    return {
      dateFrom: Moment(dateRange[0].startDate).format("YYYY-MM-DD"),
      dateTo: Moment(dateRange[0].endDate).format("YYYY-MM-DD"),
      smdId: smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : "0",
      provinceId: prov?.id && prov.id !== "999" && prov.value !== "all" ? prov.id : "0",
      cityId: city?.id && city.id !== "999" && city.value !== "all" ? city.id : "0",
    };
  };

  // Get filter parameters for top 10 sections with unified date range
  const getTopSectionsFilterParams = () => {
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");
    
    return {
      dateFrom: Moment(topSectionsDateRange[0].startDate).format("YYYY-MM-DD"),
      dateTo: Moment(topSectionsDateRange[0].endDate).format("YYYY-MM-DD"),
      smdId: smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : "0",
      provinceId: prov?.id && prov.id !== "999" && prov.value !== "all" ? prov.id : "0",
      cityId: city?.id && city.id !== "999" && city.value !== "all" ? city.id : "0",
    };
  };

  const [data, setData] = useState({
    metrics: {
      visits: 0,
      plannedVisits: 0,
      outstandingPayments: 0,
      overdueVisits: 0,
    },
    stockData: [],
    visitTrends: [],
    topStores: [],
    topProducts: [],
    topNoStocks: [],
  });

  const [loading, setLoading] = useState({
    metrics: true,
    stock: true,
    trends: true,
    stores: true,
    products: true,
    noStocks: true,
  });

  const [error, setError] = useState({
    metrics: null,
    stock: null,
    trends: null,
    stores: null,
    products: null,
    noStocks: null,
  });

  const [trendsPeriod, setTrendsPeriod] = useState("today");
  const windowDimensions = useWindowDimensions();

  // Check if any error occurred
  const hasError = Object.values(error).some((val) => val !== null);

  // Unified date range change handler for top 10 sections
  const handleTopSectionsDateRangeChange = (ranges) => {
    setTopSectionsDateRange([ranges.range1]);
  };

  // Unified date apply handler for top 10 sections
  const handleTopSectionsDateApply = () => {
    setShowTopSectionsDatePicker(false);
    setRefetch(true); // Trigger data refetch with new date range
  };

  // Fetch all dashboard data
  useEffect(() => {
    const fetchData = async () => {
      const filterParams = getFilterParams();
      
      try {
        // Fetch metrics
        setLoading((prev) => ({ ...prev, metrics: true }));
        setError((prev) => ({ ...prev, metrics: null }));
        const metricsResponse = await fetchDashboardMetrics(filterParams);
        setData((prev) => ({ ...prev, metrics: metricsResponse }));
      } catch (err) {
        console.error("Error fetching metrics:", err);
        setError((prev) => ({ ...prev, metrics: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, metrics: false }));
      }

      try {
        // Fetch top stores with unified date range
        setLoading((prev) => ({ ...prev, stores: true }));
        setError((prev) => ({ ...prev, stores: null }));
        const topSectionsFilterParams = getTopSectionsFilterParams();
        const topStoresResponse = await fetchTopStores(topSectionsFilterParams);
        setData((prev) => ({ ...prev, topStores: topStoresResponse.stores, topProducts: topStoresResponse.stocks, topNoStocks: topStoresResponse.noStocks }));
      } catch (err) {
        console.error("Error fetching top stores:", err);
        setError((prev) => ({ ...prev, stores: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, stores: false }));
      }
    };

    fetchData();
  }, [refetch]); // Add refetch as dependency

  // Separate effect for visit trends to handle period changes
  useEffect(() => {
    const fetchVisitTrendsData = async () => {
      const filterParams = getFilterParams();
      
      try {
        setLoading((prev) => ({ ...prev, trends: true }));
        setError((prev) => ({ ...prev, trends: null }));
        const trendsResponse = await fetchVisitTrends(trendsPeriod, filterParams);
        setData((prev) => ({ ...prev, visitTrends: trendsResponse }));
      } catch (err) {
        console.error("Error fetching visit trends:", err);
        setError((prev) => ({ ...prev, trends: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, trends: false }));
      }
    };

    fetchVisitTrendsData();
  }, [trendsPeriod, refetch]); // Add refetch as dependency

  // Effect to refetch data when filters change
  useEffect(() => {
    if (refetch) {
      setRefetch(false);
    }
  }, [refetch]);

  // Effect to listen for localStorage changes and trigger refetch
  useEffect(() => {
    const handleStorageChange = () => {
      setRefetch(true);
    };

    // Listen for storage events (when localStorage changes in other tabs)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check for changes periodically (for same-tab changes)
    const interval = setInterval(() => {
      const currentSmd = ls.get("smd");
      const currentProv = ls.get("prov");
      const currentCity = ls.get("city");
      
      // Store current values to compare
      if (!window.lastFilterValues) {
        window.lastFilterValues = { smd: currentSmd, prov: currentProv, city: currentCity };
      } else {
        const lastValues = window.lastFilterValues;
        if (
          JSON.stringify(currentSmd) !== JSON.stringify(lastValues.smd) ||
          JSON.stringify(currentProv) !== JSON.stringify(lastValues.prov) ||
          JSON.stringify(currentCity) !== JSON.stringify(lastValues.city)
        ) {
          window.lastFilterValues = { smd: currentSmd, prov: currentProv, city: currentCity };
          setRefetch(true);
        }
      }
    }, 1000); // Check every second

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleTrendsPeriodChange = (period) => {
    setTrendsPeriod(period);
  };

  const getTrendsTitle = () => {
    switch (trendsPeriod) {
      case "today":
        return "Today's Visit Trends (Hourly)";
      case "7days":
        return "Visit Trends (Last 7 Days)";
      case "biweekly":
        return "Visit Trends (Last 14 Days)";
      case "month":
        return "Visit Trends (Last 30 Days)";
      default:
        return "Visit Trends";
    }
  };

  return (
    <div style={{ color: "black" }}>
      <SalesPicker onFilterChange={() => setRefetch(true)} />
      
      {/* Filter Summary */}
      <CRow className="mb-3">
        <CCol>
          <CCard>
            <CCardBody className="py-2">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <strong className="me-3">Active Filters:</strong>
                  <div className="d-flex gap-2 flex-wrap">
                    {ls.get("smd")?.id && ls.get("smd").id !== "999" && ls.get("smd").value !== "all" && (
                      <CBadge color="primary">
                        SMD: {ls.get("smd").label}
                      </CBadge>
                    )}
                    {ls.get("prov")?.id && ls.get("prov").id !== "999" && ls.get("prov").value !== "all" && (
                      <CBadge color="success">
                        Province: {ls.get("prov").label}
                      </CBadge>
                    )}
                    {ls.get("city")?.id && ls.get("city").id !== "999" && ls.get("city").value !== "all" && (
                      <CBadge color="warning">
                        City: {ls.get("city").label}
                      </CBadge>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <CButton
                    size="sm"
                    color="outline-secondary"
                    onClick={() => {
                      // Reset date range to current month
                      const newDateRange = [{
                        startDate: new Date(`${Moment(new Date()).startOf('month')}`),
                        endDate: new Date()
                      }];
                      setDateRange(newDateRange);
                      setTopSectionsDateRange(newDateRange);
                      ls.set("dateRange", newDateRange[0]);
                      
                      // Reset SMD/Province/City filters
                      ls.set("smd", {
                        id: "0",
                        label: "All SMD",
                        value: "all",
                      });
                      ls.set("prov", {
                        id: "0",
                        label: "All Provinces",
                        value: "all",
                      });
                      ls.set("city", {
                        id: "0",
                        label: "All Cities",
                        value: "all",
                      });
                      
                      setRefetch(true);
                    }}
                  >
                    Clear All Filters
                  </CButton>
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      
      {/* Top Metrics Cards */}
      <CRow className="mb-4">
        <CCol md={3}>
          <CWidgetStatsA
            color="primary"
            value={data.metrics.visits}
            title="Today's Visits"
            icon={<CIcon icon={cilPeople} height={36} />}
          />
        </CCol>
        <CCol md={3}>
          <CWidgetStatsA
            color="info"
            value={data.metrics.plannedVisits}
            title="Planned Visits"
            icon={<CIcon icon={cilCalendar} height={36} />}
          />
        </CCol>
        <CCol md={3}>
          <CWidgetStatsA
            color="warning"
            value={formatter("currency", data.metrics.outstandingPayments)}
            title="Outstanding Payments"
            icon={<CIcon icon={cilChart} height={36} />}
          />
        </CCol>
        <CCol md={3}>
          <CWidgetStatsA
            color="danger"
            value={data.metrics.overdueVisits}
            title="Overdue Visits"
            icon={<CIcon icon={cibAppStore} height={36} />}
          />
        </CCol>
      </CRow>

      {/* Visit Trends */}
      <CRow className="mb-4">
        <CCol>
        <CCard>
          <CCardHeader className="d-flex justify-content-between align-items-center">
            <h5>{getTrendsTitle()}</h5>
            <CButtonGroup>
              <CButton
                color="outline-secondary"
                size="sm"
                active={trendsPeriod === "today"}
                onClick={() => handleTrendsPeriodChange("today")}
              >
                Today
              </CButton>
              <CButton
                color="outline-secondary"
                size="sm"
                active={trendsPeriod === "7days"}
                onClick={() => handleTrendsPeriodChange("7days")}
              >
                7 Days
              </CButton>
              <CButton
                color="outline-secondary"
                size="sm"
                active={trendsPeriod === "biweekly"}
                onClick={() => handleTrendsPeriodChange("biweekly")}
              >
                14 Days
              </CButton>
              <CButton
                color="outline-secondary"
                size="sm"
                active={trendsPeriod === "month"}
                onClick={() => handleTrendsPeriodChange("month")}
              >
                30 Days
              </CButton>
            </CButtonGroup>
          </CCardHeader>
          <CCardBody>
            {loading.trends ? (
              <div className="text-center py-4">
                <CSpinner />
              </div>
            ) : (
              <CChartBar
                height={"100px"}
                data={{
                  labels: data.visitTrends.map((t) => t.date),
                  datasets: [
                    {
                      label: "Completed Visits",
                      backgroundColor: "rgba(78, 200, 51, 0.8)",
                      borderColor: "#4E5B97",
                      data: data.visitTrends.map((t) => t.completed),
                    },
                    {
                      label: "Planned Visits",
                      backgroundColor: "rgba(48, 111, 186, 0.8)",
                      borderColor: "#306fba",
                      data: data.visitTrends.map((t) => t.planned),
                    },
                  ],
                }}
                options={{
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        precision: 0,
                      },
                    },
                  },
                }}
              />
            )}
          </CCardBody>
        </CCard>
        </CCol>
      </CRow>

      {/* Top 10 Sections with Unified Date Range Picker */}
      <CRow className="mb-3">
        <CCol>
          <CCard>
            <CCardHeader>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                <h5 className="mb-0">Top 10 Analytics</h5>
                <div className="d-flex gap-2">
                  <CButton
                    color="primary"
                    size="sm"
                    onClick={() => setShowTopSectionsDatePicker(!showTopSectionsDatePicker)}
                  >
                    <CIcon icon={cilCalendar} className="me-1" />
                    {format(topSectionsDateRange[0].startDate, "MMM d")} – {format(topSectionsDateRange[0].endDate, "MMM d, yyyy")}
                  </CButton>
                </div>
              </div>
            </CCardHeader>
            
            {showTopSectionsDatePicker && (
              <CCardBody className="date-picker-container p-3">
                <DateRangePicker
                  onChange={handleTopSectionsDateRangeChange}
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                  months={1}
                  ranges={topSectionsDateRange}
                  direction="horizontal"
                  className="compact-date-picker"
                />
                <div className="text-right mt-2">
                  <CButton
                    color="secondary"
                    size="sm"
                    onClick={() => setShowTopSectionsDatePicker(false)}
                    className="mr-2"
                  >
                    Cancel
                  </CButton>
                  <CButton color="primary" size="sm" onClick={handleTopSectionsDateApply}>
                    Apply
                  </CButton>
                </div>
              </CCardBody>
            )}
          </CCard>
        </CCol>
      </CRow>

      <CRow className="mb-4">
        <CCol md={4}>
          <CCard className="h-100">
            <CCardHeader>
              <h5>Top 10 Visited Stores</h5>
            </CCardHeader>
            <CCardBody>
              {loading.stores ? (
                <div className="text-center py-4">
                  <CSpinner />
                </div>
              ) : data.topStores.length > 0 ? (
                data.topStores.map((store, index) => (
                  <a
                    href={"/stores/details/" + store.store_id}
                    key={store.store_id}
                    className="text-decoration-none text-dark"
                  >
                    <div key={store.store_id} className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>
                          <CBadge color="primary" className="me-2">
                            {index + 1}
                          </CBadge>
                          {store.storeName}
                        </span>
                        <CBadge color="secondary">
                          {store.visitCount} visits
                        </CBadge>
                      </div>
                    </div>
                  </a>
                ))
              ) : (
                <div className="text-center text-muted py-4">
                  No store data available
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={4}>
          <CCard className="h-100">
            <CCardHeader>
              <h5>Top 10 Stocked Products</h5>
            </CCardHeader>
            <CCardBody>
              {loading.stores ? (
                <div className="text-center py-4">
                  <CSpinner />
                </div>
              ) : data.topProducts.length > 0 ? (
                data.topProducts.map((stock, index) => (
                  <a
                    href={"/product/details/" + stock.id}
                    key={stock.id}
                    className="text-decoration-none text-dark"
                  >
                    <div key={stock.id} className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>
                          <CBadge color="primary" className="me-2">
                            {index + 1}
                          </CBadge>
                          {stock.product_name}
                        </span>
                        <CBadge color="secondary">
                          {stock.storeCount} stores
                        </CBadge>
                      </div>
                    </div>
                  </a>
                ))
              ) : (
                <div className="text-center text-muted py-4">
                  No store data available
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={4}>
          <CCard className="h-100">
            <CCardHeader>
              <h5>Top 10 Out of Stock Products</h5>
            </CCardHeader>
            <CCardBody>
              {loading.stores ? (
                <div className="text-center py-4">
                  <CSpinner />
                </div>
              ) : data.topNoStocks.length > 0 ? (
                data.topNoStocks.map((noStock, index) => (
                  <a
                    href={"/product/details/" + noStock.id}
                    key={noStock.id}
                    className="text-decoration-none text-dark"
                  >
                    <div key={noStock.id} className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>
                          <CBadge color="primary" className="me-2">
                            {index + 1}
                          </CBadge>
                          {noStock.product_name}
                        </span>
                        <CBadge color="secondary">
                          {noStock.storeCount} stores
                        </CBadge>
                      </div>
                    </div>
                  </a>
                ))
              ) : (
                <div className="text-center text-muted py-4">
                  No store data available
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>
  );
}

// API functions
async function fetchDashboardMetrics(params) {
  try {
    const response = await getDashboardMetrics(params);
    if (response.success) {
      return (
        response.metrics || {
          visits: 0,
          plannedVisits: 0,
          outstandingPayments: 0,
          overdueVisits: 0,
        }
      );
    }
    throw new Error(response.message || "Failed to fetch metrics");
  } catch (error) {
    console.error("Error in fetchDashboardMetrics:", error);
    throw error;
  }
}

async function fetchVisitTrends(period = "today", params) {
  try {
    const response = await getVisitTrends(period, params);
    if (response.success) {
      return response.data || [];
    }
    throw new Error(response.message || "Failed to fetch visit trends");
  } catch (error) {
    console.error("Error in fetchVisitTrends:", error);
    throw error;
  }
}

async function fetchTopStores(params) {
  try {
    const response = await getMostVisitedStores(params);
    if (response.success) {
      return response || [];
    }
    throw new Error(response.message || "Failed to fetch most visited stores");
  } catch (error) {
    console.error("Error in fetchVisitTrends:", error);
    throw error;
  }
}

export default Dashboard;
